import React, { useEffect, useState } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFieldNumber,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiHorizontalRule,
  EuiPanel,
  EuiSelect,
  EuiSpacer,
  EuiSwitch,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useApi } from "../";
import { useAnketaAuth } from "../auth/context";
import { useAnketaI18n } from "src/internationalization";
import { TableSkeleton } from "src/components/skeleton";

type SyslogProtocol = "udp" | "tcp";

interface SyslogDestination {
  name: string | null;
  host: string;
  port: number;
  protocol: SyslogProtocol;
  enabled: boolean;
}

interface SyslogDestinationsResponse {
  destinations: SyslogDestination[];
  max: number;
}

const emptyDestination = (): SyslogDestination => ({
  name: "",
  host: "",
  port: 514,
  protocol: "udp",
  enabled: true,
});

/**
 * Editor for the Wazuh syslog destinations (superuser-only). Configures up to
 * `max` destinations (host/port/protocol/enabled) that the backend audit emitter
 * ships security events to. Saving PUTs the whole list to
 * /api/v2/admin/syslog-destinations; the backend caps and renumbers it.
 */
export const AdminSyslogDestinations = (): JSX.Element => {
  const { Get, Put } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<boolean>(false);

  const [max, setMax] = useState<number>(3);
  const [destinations, setDestinations] = useState<SyslogDestination[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await Get<any>({
        path: "v2/admin/syslog-destinations",
        responseType: "json",
        token: keycloak?.token,
      });
      const payload: SyslogDestinationsResponse | null =
        (resp?.data as any)?.data ?? null;
      if (!payload) {
        setError(
          translate("anketa.admin.syslog.load.error", {
            defaultMessage: "Destinations could not be loaded.",
          }),
        );
        return;
      }
      setMax(payload.max ?? 3);
      setDestinations(
        (payload.destinations ?? []).map((d) => ({
          name: d.name ?? "",
          host: d.host,
          port: d.port,
          protocol: d.protocol === "tcp" ? "tcp" : "udp",
          enabled: d.enabled !== false,
        })),
      );
    } catch (e) {
      setError(
        translate("anketa.admin.syslog.load.error", {
          defaultMessage: "Destinations could not be loaded.",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak?.token) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const update = (index: number, patch: Partial<SyslogDestination>) => {
    setSaved(false);
    setDestinations((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    );
  };

  const remove = (index: number) => {
    setSaved(false);
    setDestinations((prev) => prev.filter((_, i) => i !== index));
  };

  const add = () => {
    setSaved(false);
    setDestinations((prev) => [...prev, emptyDestination()]);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const resp = await Put<any>({
        path: "v2/admin/syslog-destinations",
        responseType: "json",
        token: keycloak?.token,
        value: {
          destinations: destinations.map((d) => ({
            name: d.name?.trim() ? d.name.trim() : null,
            host: d.host.trim(),
            port: Number(d.port),
            protocol: d.protocol,
            enabled: d.enabled,
          })),
        },
      });
      if (resp?.success) {
        setSaved(true);
        await load();
      } else {
        const details: string[] | undefined = (resp?.data as any)?.data?.details;
        setError(
          details?.length
            ? details.join(" · ")
            : translate("anketa.admin.save.error", {
                defaultMessage: "Saving failed.",
              }),
        );
      }
    } catch (e) {
      setError(
        translate("anketa.admin.save.error", {
          defaultMessage: "Saving failed.",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={8} />;
  }

  return (
    <>
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.syslog.description", {
          values: { max },
          defaultMessage:
            "Security events (logins, vault accesses, creating and archiving questionnaires) are sent as syslog (JSON) to the Wazuh destinations configured here. Up to {{max}} destinations are possible. Without an active destination nothing is sent.",
        })}
      </EuiText>
      <EuiSpacer size="m" />

      {error && (
        <>
          <EuiCallOut title={error} color="danger" iconType="alert" />
          <EuiSpacer size="m" />
        </>
      )}
      {saved && (
        <>
          <EuiCallOut
            title={translate("anketa.admin.syslog.saved", {
              defaultMessage: "Destinations saved.",
            })}
            color="success"
            iconType="check"
          />
          <EuiSpacer size="m" />
        </>
      )}

      {destinations.length === 0 && (
        <EuiText size="s" color="subdued">
          {translate("anketa.admin.syslog.empty", {
            defaultMessage: "No destinations configured.",
          })}
        </EuiText>
      )}

      {destinations.map((dest, index) => (
        <React.Fragment key={index}>
          <EuiPanel hasShadow={false} hasBorder paddingSize="m">
            <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
              <EuiFlexItem grow={false}>
                <EuiTitle size="xxs">
                  <h4>
                    {translate("anketa.admin.syslog.destination.title", {
                      values: { index: index + 1 },
                      defaultMessage: "Destination {{index}}",
                    })}
                  </h4>
                </EuiTitle>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiSwitch
                  label={translate("anketa.admin.syslog.enabled.label", {
                    defaultMessage: "Active",
                  })}
                  checked={dest.enabled}
                  onChange={(e) => update(index, { enabled: e.target.checked })}
                />
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="s" />

            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate("anketa.admin.syslog.name.label", {
                    defaultMessage: "Name (optional)",
                  })}
                >
                  <EuiFieldText
                    placeholder={translate(
                      "anketa.admin.syslog.name.placeholder",
                      { defaultMessage: "e.g. Wazuh Primary" },
                    )}
                    value={dest.name ?? ""}
                    onChange={(e) => update(index, { name: e.target.value })}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate("anketa.admin.syslog.host.label", {
                    defaultMessage: "Host",
                  })}
                >
                  <EuiFieldText
                    placeholder="wazuh.example.net"
                    value={dest.host}
                    onChange={(e) => update(index, { host: e.target.value })}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiFormRow
                  label={translate("anketa.admin.syslog.port.label", {
                    defaultMessage: "Port",
                  })}
                >
                  <EuiFieldNumber
                    value={dest.port}
                    min={1}
                    max={65535}
                    onChange={(e) =>
                      update(index, { port: Number(e.target.value) })
                    }
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiFormRow
                  label={translate("anketa.admin.syslog.protocol.label", {
                    defaultMessage: "Protocol",
                  })}
                >
                  <EuiSelect
                    options={[
                      { value: "udp", text: "UDP" },
                      { value: "tcp", text: "TCP" },
                    ]}
                    value={dest.protocol}
                    onChange={(e) =>
                      update(index, {
                        protocol: e.target.value === "tcp" ? "tcp" : "udp",
                      })
                    }
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="s" />
            <EuiButtonEmpty
              color="danger"
              iconType="trash"
              size="s"
              onClick={() => remove(index)}
            >
              {translate("anketa.admin.syslog.remove.button", {
                defaultMessage: "Remove",
              })}
            </EuiButtonEmpty>
          </EuiPanel>
          <EuiSpacer size="m" />
        </React.Fragment>
      ))}

      {destinations.length < max && (
        <>
          <EuiButtonEmpty iconType="plusInCircle" onClick={add}>
            {translate("anketa.admin.syslog.add.button", {
              defaultMessage: "Add destination",
            })}
          </EuiButtonEmpty>
          <EuiSpacer size="m" />
        </>
      )}

      <EuiHorizontalRule />

      <EuiButton fill onClick={save} isLoading={saving}>
        {translate("anketa.admin.save.button", {
          defaultMessage: "Save",
        })}
      </EuiButton>{" "}
      <EuiButton onClick={load} isDisabled={saving}>
        {translate("anketa.admin.reset.button", {
          defaultMessage: "Reset",
        })}
      </EuiButton>
    </>
  );
};
